import type {
  GapAnalysisMissedSource,
  GetGapExecutionApiResponse,
} from '@momentum/types';

/** One source a validator says the finder never opened. */
function MissedSourceCard({ source }: { source: GapAnalysisMissedSource }) {
  return (
    <div className="gr-cand">
      <div className="gr-cand-top">
        <span className="gr-chip">{source.sourceScope}</span>
        {source.affectedScopes !== undefined &&
          source.affectedScopes.length > 0 && (
            <span className="gr-chip">
              affects {source.affectedScopes.join(', ')}
            </span>
          )}
      </div>
      <span className="gr-cand-k">item</span>
      <p className="gr-cand-reason">{source.item}</p>
      <span className="gr-cand-k">location</span>
      <p className="gr-cand-reason">{source.location}</p>
      <span className="gr-cand-k">enables</span>
      <p className="gr-cand-claim">{source.enables}</p>
    </div>
  );
}

/**
 * The find⇄validate loop's own record.
 *
 * An execution is the whole loop, capped at three runs, not one Workflow
 * invocation — so `iterations` is the interesting part and `runsExecuted` is a
 * summary of it. Missed sources sit beside it because they are what makes the
 * next run worth executing: a loop that stopped at one run stopped because
 * nothing here could enable a new gap.
 */
export function RunLoopSection({
  execution,
}: {
  execution: GetGapExecutionApiResponse;
}) {
  const missedSources = execution.iterations.flatMap(
    (iteration) => iteration.missedSources
  );
  const quarantinedSources = execution.iterations.flatMap(
    (iteration) => iteration.quarantinedSources
  );
  const isSingleRun = execution.runsExecuted === 1;

  return (
    <section>
      <div className="gr-rule gr-rule-flush">
        <span>The loop</span>
        <i />
      </div>

      <div className="gr-cols2">
        <div>
          <div className="gr-stack">
            {execution.iterations.map((iteration) => (
              <div className="gr-card" key={iteration.runNumber}>
                <div className="gr-kv">
                  <span>runNumber</span>
                  <span>{iteration.runNumber}</span>
                </div>
                <div className="gr-kv">
                  <span>addendum</span>
                  <span>
                    {iteration.addendum.length === 0
                      ? 'none'
                      : `${iteration.addendum.length} chars`}
                  </span>
                </div>
                <div className="gr-kv">
                  <span>gapIdsFound</span>
                  <span>{iteration.gapIdsFound.length}</span>
                </div>
                <div className="gr-kv">
                  <span>missedSources</span>
                  <span>{iteration.missedSources.length}</span>
                </div>
                <div className="gr-kv">
                  <span>quarantinedSources</span>
                  <span>{iteration.quarantinedSources.length || 'none'}</span>
                </div>
                <div className="gr-kv">
                  <span>failedScopes</span>
                  <span>
                    {iteration.failedScopes.length === 0
                      ? 'none'
                      : iteration.failedScopes.join(', ')}
                  </span>
                </div>
              </div>
            ))}

            <div className="gr-card">
              <div className="gr-kv">
                <span>runsExecuted</span>
                <span>{execution.runsExecuted}</span>
              </div>
              <div className="gr-kv">
                <span>stopReason</span>
                <span>{execution.stopReason}</span>
              </div>
              <div className="gr-kv">
                <span>failedScopes (execution)</span>
                <span>
                  {execution.failedScopes.length === 0
                    ? 'none'
                    : execution.failedScopes
                        .map((failed) => failed.slug)
                        .join(', ')}
                </span>
              </div>
            </div>
          </div>

          {isSingleRun && (
            <p className="gr-cand-claim gr-stack-top-lg">
              This execution ran a single iteration, so there is no second-run
              story to read: every gap id it produced carries the{' '}
              <code>R1-</code> prefix, and the addendum — the loop&apos;s only
              feedback channel, injected into run 2&apos;s finders to chase the
              missed sources listed here — was never written.
            </p>
          )}
        </div>

        <div className="gr-stack">
          <div>
            <span className="gr-card-k">
              missedSources · {missedSources.length}
            </span>
            <p className="gr-cand-claim gr-stack-top-sm">
              Raised by a validator as a possible rerun trigger: a document,
              record or analog project the finder never opened. Not evidence
              against a gap that already exists.
            </p>
          </div>

          {missedSources.length === 0 ? (
            <div className="gr-empty">
              <b>None raised</b>
              No validator reported a source the finder had missed.
            </div>
          ) : (
            missedSources.map((source) => (
              <MissedSourceCard
                key={source.location + source.item}
                source={source}
              />
            ))
          )}

          {quarantinedSources.length > 0 && (
            <>
              <span className="gr-card-k">
                quarantinedSources · {quarantinedSources.length}
              </span>
              {quarantinedSources.map((quarantined) => (
                <div
                  className="gr-cand"
                  key={quarantined.source.location + quarantined.source.item}
                >
                  <div className="gr-cand-top">
                    <span className="gr-chip" data-tone="amber">
                      quarantined
                    </span>
                  </div>
                  <span className="gr-cand-k">reason</span>
                  <p className="gr-cand-reason">{quarantined.reason}</p>
                  <span className="gr-cand-k">item</span>
                  <p className="gr-cand-claim">{quarantined.source.item}</p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
